import { useEffect } from 'react';
import type { TypedDocumentNode } from 'urql';

import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { useEnsureFreshData } from '@packages/shared/src/hooks/useEnsureFreshData/useEnsureFreshData';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';

export type UseBadgeCountOptions<TResult, TVariables> = {
  /*
   * The GraphQL document to execute.
   * This should be a TypedDocumentNode with the expected result and variables types.
   */
  document: TypedDocumentNode<TResult, TVariables>;
  /*
   * A function that takes the result of the query and returns the count.
   * This function should be defined in the component using this hook.
   */
  getCount: (data: TResult | undefined) => number;
  /*
   * The name of the window event to listen for external updates. When this event is triggered, the query will be re-executed.
   */
  eventNameForExternalUpdates: string;
};

/**
 * Generic hook to fetch and refetch a badge count for basket/wishlist slots.
 */
export function useBadgeCount<TResult, TVariables extends { locale: string }>({
  document,
  getCount,
  eventNameForExternalUpdates,
}: UseBadgeCountOptions<TResult, TVariables>) {
  const { locale } = useI18n();
  const [{ data, fetching }, executeQuery] = useAuthQuery({
    query: document,
    variables: { locale } as TVariables,
    requestPolicy: 'cache-and-network',
  });

  useEnsureFreshData({
    refetch: () => executeQuery({ requestPolicy: 'network-only' }),
  });

  useEffect(() => {
    const eventListener: EventListener = () => {
      executeQuery({ requestPolicy: 'network-only' });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener(eventNameForExternalUpdates, eventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener(eventNameForExternalUpdates, eventListener);
      }
    };
  }, [executeQuery, eventNameForExternalUpdates]);

  return {
    badgeCount: !fetching && data ? getCount(data) : undefined,
  };
}
